/**
 * Simplified Cart Add-on Toggle Management
 * Reduces multiple loading states and leverages server-side rendering
 */

class CartAddonToggle {
  constructor() {
    this.activeRequests = new Map(); // Track active requests by key
    this.cartUpdateUnsubscriber = null;
    this.init();
  }

  init() {
    // Single delegated event listener for checkbox changes
    document.addEventListener('change', this.handleChange.bind(this));
    
    // Single delegated event listener for collapsible toggles
    document.addEventListener('click', this.handleClick.bind(this));
    
    // Subscribe to theme's pubsub system for cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-addon-toggle') {
        this.handleCartUpdated();
      }
    });

    // Also listen for legacy cartUpdated events for backward compatibility
    document.addEventListener('cartUpdated', this.handleCartUpdated.bind(this));
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleChange(event) {
    const checkbox = event.target;
    if (!checkbox.classList.contains('cart-addon-toggle__checkbox')) return;
    
    // Prevent multiple simultaneous requests for the same addon
    const key = `${checkbox.dataset.variantId}-${checkbox.dataset.mainVariantId}`;
    if (this.activeRequests.has(key)) {
      event.preventDefault();
      checkbox.checked = !checkbox.checked;
      return;
    }
    
    this.toggleAddon(checkbox);
  }

  handleClick(event) {
    const header = event.target.closest('[data-toggle-header]');
    if (!header || event.target.closest('.cart-addon-toggle__checkbox-wrapper')) return;
    
    this.toggleDetails(header);
  }

  async toggleAddon(checkbox) {
    const toggle = checkbox.closest('.cart-addon-toggle');
    const key = `${checkbox.dataset.variantId}-${checkbox.dataset.mainVariantId}`;
    
    // Mark as loading immediately via CSS
    toggle.classList.add('is-loading');
    this.activeRequests.set(key, true);
    
    try {
      const isChecked = checkbox.checked;
      const variantId = checkbox.dataset.variantId;
      const mainVariantId = checkbox.dataset.mainVariantId;
      const mainProductId = checkbox.dataset.mainProductId;
      const quantity = parseInt(checkbox.dataset.mainQuantity) || 1;
      
      let result;
      if (isChecked) {
        // Add addon
        result = await this.addAddon(variantId, mainVariantId, mainProductId, quantity);
      } else {
        // Remove addon using the key from data attribute
        const addonKey = checkbox.dataset.addonKey;
        if (addonKey) {
          result = await this.removeAddon(addonKey);
        } else {
          // Fallback: find and remove by variant/main IDs
          result = await this.removeAddonByVariant(variantId, mainVariantId, mainProductId);
        }
      }
      
      // Publish cart update event to trigger theme's cart refresh
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-addon-toggle',
        cartData: result,
        action: isChecked ? 'add' : 'remove',
        variantId: variantId
      });
      
    } catch (error) {
      console.error('Failed to toggle addon:', error);
      // Revert checkbox on error
      checkbox.checked = !checkbox.checked;
      toggle.classList.remove('is-loading');
      
      // Show error message if available
      this.showError(toggle, error.message || 'Failed to update cart');
    } finally {
      this.activeRequests.delete(key);
    }
  }

  async addAddon(variantId, mainVariantId, mainProductId, quantity) {
    const formData = {
      items: [{
        id: variantId,
        quantity: quantity,
        properties: {
          '_upsell_of': mainVariantId,
          '_bound_to_product': mainProductId,
          '_is_hidden_upsell': 'true'
        }
      }]
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || `Failed to add addon: ${response.status}`);
    }

    return response.json();
  }

  async removeAddon(addonKey) {
    const updates = { [addonKey]: 0 };
    
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || `Failed to remove addon: ${response.status}`);
    }

    return response.json();
  }

  async removeAddonByVariant(variantId, mainVariantId, mainProductId) {
    // Fallback method when addon key is not available
    try {
      const cart = await this.getCart();
      const updates = {};

      cart.items.forEach(item => {
        if (item.variant_id.toString() === variantId && 
            item.properties && 
            item.properties._upsell_of === mainVariantId &&
            item.properties._bound_to_product === mainProductId) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        console.warn('Addon item not found for removal');
        return { items: cart.items };
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove addon: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error in removeAddonByVariant:', error);
      throw error;
    }
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get cart:', error);
      throw error;
    }
  }

  handleCartUpdated() {
    // Remove all loading states when cart is updated
    // The server has already rendered the correct state
    document.querySelectorAll('.cart-addon-toggle.is-loading').forEach(toggle => {
      toggle.classList.remove('is-loading');
    });
    
    // Clear any pending requests
    this.activeRequests.clear();

    // Clear any error messages
    document.querySelectorAll('.cart-addon-toggle__error').forEach(error => {
      error.remove();
    });
  }

  showError(toggle, message) {
    // Remove any existing error
    const existingError = toggle.querySelector('.cart-addon-toggle__error');
    if (existingError) {
      existingError.remove();
    }

    // Create and show error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-addon-toggle__error';
    errorDiv.textContent = message;
    toggle.appendChild(errorDiv);

    // Auto-remove error after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }

  toggleDetails(header) {
    const toggle = header.closest('.cart-addon-toggle');
    const details = toggle.querySelector('[data-toggle-details]');
    const arrow = toggle.querySelector('.cart-addon-toggle__arrow');
    
    if (!details || !arrow) return;
    
    const isHidden = details.style.display === 'none' || details.style.display === '';
    
    details.style.display = isHidden ? 'block' : 'none';
    arrow.classList.toggle('rotated', isHidden);
    toggle.classList.toggle('expanded', isHidden);
  }
}

// Initialize once when DOM is ready
function initCartAddonToggle() {
  if (!window.cartAddonToggle) {
    window.cartAddonToggle = new CartAddonToggle();
  }
}

// Initialize immediately if DOM is already ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartAddonToggle);
} else {
  initCartAddonToggle();
}

// Also ensure it's initialized after theme scripts load
document.addEventListener('theme:loaded', initCartAddonToggle);